import React, { useState, useEffect, useCallback } from 'react';
import { vehicleExpenseAPI, transportAPI } from '../../services/api';
import { notify, confirmDialog } from '../../utils/notify';

const CATEGORY_OPTIONS = [
  { value: 'SERVICING', label: '🛠️ Servicing' },
  { value: 'MAINTENANCE_REPAIR', label: '🔧 Maintenance & Repairs' },
  { value: 'FUEL', label: '⛽ Fuel / Refill Expense' },
  { value: 'INSURANCE_RENEWAL', label: '🛡️ Insurance Renewal' },
  { value: 'PUC_RENEWAL', label: '🌱 Pollution (PUC) Renewal' },
  { value: 'PERMIT_RENEWAL', label: '📜 Transport Permit Renewal' },
  { value: 'REGISTRATION_RENEWAL', label: '🪪 Registration Renewal' },
  { value: 'OIL_CHANGE', label: '🛢️ Engine Oil Change' },
  { value: 'TYRE_REPLACEMENT', label: '🛞 Tyre Replacement' },
  { value: 'BATTERY_REPLACEMENT', label: '🔋 Battery Replacement' },
  { value: 'SPARE_PARTS', label: '⚙️ Spare Parts' },
  { value: 'CLEANING_WASHING', label: '🧼 Vehicle Cleaning & Washing' },
  { value: 'EMERGENCY_REPAIR', label: '🚨 Emergency Repair' },
  { value: 'OTHER', label: '📦 Other Expense' },
];

const STATUTORY_CATEGORIES = [
  'INSURANCE_RENEWAL',
  'PUC_RENEWAL',
  'PERMIT_RENEWAL',
  'REGISTRATION_RENEWAL',
];

const STATUS_BADGES = {
  DRAFT: { label: 'Draft', bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
  SUBMITTED: { label: 'Pending Finance Approval', bg: '#fef3c7', color: '#b45309', border: '#fde68a' },
  CORRECTION_REQUESTED: { label: 'Correction Requested', bg: '#ffedd5', color: '#c2410c', border: '#fed7aa' },
  APPROVED: { label: 'Approved & Released', bg: '#dbeafe', color: '#1d4ed8', border: '#bfdbfe' },
  REJECTED: { label: 'Rejected by Finance', bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5' },
  COMPLETED: { label: 'Completed & Settled', bg: '#dcfce7', color: '#15803d', border: '#86efac' },
};

const TransportVehicleExpensesTab = () => {
  const [expenses, setExpenses] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState({
    total_requested: 0,
    total_approved: 0,
    pending_count: 0,
    completed_count: 0,
    status_counts: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  // Modals state
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [submittingId, setSubmittingId] = useState(null);

  // Request Form state
  const [formVehicle, setFormVehicle] = useState('');
  const [formCategory, setFormCategory] = useState('SERVICING');
  const [formAmount, setFormAmount] = useState('');
  const [formVendor, setFormVendor] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formOdoReading, setFormOdoReading] = useState('');
  const [formQuotation, setFormQuotation] = useState(null);
  const [formInitialBill, setFormInitialBill] = useState(null);
  const [formRemarks, setFormRemarks] = useState('');
  const [formSubmitDirectly, setFormSubmitDirectly] = useState(false);
  const [formSaving, setFormSaving] = useState(false);

  // Fuel Refill ODO verification state
  const [fuelInfo, setFuelInfo] = useState(null);
  const [loadingFuelInfo, setLoadingFuelInfo] = useState(false);

  // Finalize / Settlement Modal
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completingExpense, setCompletingExpense] = useState(null);
  const [finalInvoice, setFinalInvoice] = useState(null);
  const [receiptCopy, setReceiptCopy] = useState(null);
  const [renewedDoc, setRenewedDoc] = useState(null);
  const [newExpiryDate, setNewExpiryDate] = useState('');
  const [completeNotes, setCompleteNotes] = useState('');
  const [completing, setCompleting] = useState(false);

  // Detail / Audit Log Modal
  const [detailExpense, setDetailExpense] = useState(null);

  const fetchFuelInfo = useCallback(async (vehId) => {
    if (!vehId) {
      setFuelInfo(null);
      return;
    }
    setLoadingFuelInfo(true);
    try {
      const data = await vehicleExpenseAPI.getLatestFuelInfo(vehId);
      setFuelInfo(data);
      if (data && data.current_vehicle_odo !== undefined) {
        setFormOdoReading(String(data.current_vehicle_odo));
      }
    } catch (err) {
      console.error('Failed to fetch fuel info:', err);
      setFuelInfo(null);
    } finally {
      setLoadingFuelInfo(false);
    }
  }, []);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.expense_category = categoryFilter;
      if (vehicleFilter) params.vehicle_id = vehicleFilter;
      if (search) params.search = search;
      if (dateFilter) params.date = dateFilter;

      const [data, statsData, vehData] = await Promise.all([
        vehicleExpenseAPI.getExpenses(params),
        vehicleExpenseAPI.getStats(),
        transportAPI.getVehicles(),
      ]);

      setExpenses(Array.isArray(data) ? data : data.results || []);
      setStats(statsData);
      setVehicles(Array.isArray(vehData) ? vehData : vehData.results || []);
    } catch (err) {
      console.error('Failed to fetch vehicle expenses:', err);
      setError('Failed to load vehicle expenses. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, vehicleFilter, search, dateFilter]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleOpenCreateModal = () => {
    setEditingExpense(null);
    setFuelInfo(null);
    const initialVehId = vehicles.length > 0 ? vehicles[0].id : '';
    const initialVeh = vehicles.find((v) => String(v.id) === String(initialVehId));
    setFormVehicle(initialVehId);
    setFormOdoReading(initialVeh ? (initialVeh.odometer_reading || initialVeh.odo_meter_reading || '') : '');
    setFormCategory('SERVICING');
    setFormAmount('');
    setFormVendor('');
    setFormDescription('');
    setFormQuotation(null);
    setFormInitialBill(null);
    setFormRemarks('');
    setFormSubmitDirectly(false);
    setShowRequestModal(true);
  };

  const handleOpenEditModal = (exp) => {
    setEditingExpense(exp);
    setFormVehicle(exp.vehicle);
    const cat = exp.expense_category || exp.category || 'SERVICING';
    setFormCategory(cat);
    setFormAmount(exp.requested_amount);
    setFormVendor(exp.vendor_name || '');
    setFormDescription(exp.expense_description || exp.description || '');
    setFormOdoReading(exp.odo_reading !== undefined && exp.odo_reading !== null ? exp.odo_reading : (exp.odometer_reading || ''));
    setFormQuotation(null);
    setFormInitialBill(null);
    setFormRemarks(exp.transport_manager_remarks || exp.remarks || '');
    setFormSubmitDirectly(false);
    setShowRequestModal(true);
    if (cat === 'FUEL' && exp.vehicle) {
      fetchFuelInfo(exp.vehicle);
    } else {
      setFuelInfo(null);
    }
  };

  const handleSaveRequest = async (e) => {
    e.preventDefault();
    if (!formVehicle || !formAmount || parseFloat(formAmount) <= 0) {
      notify.error('Please select a vehicle and enter a valid requested amount.');
      return;
    }

    if (formCategory === 'FUEL') {
      if (formOdoReading === '' || formOdoReading === null || parseInt(formOdoReading, 10) < 0) {
        notify.success('Current ODO meter reading is required for Fuel / Refill Expense requests.');
        return;
      }
    }

    setFormSaving(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('vehicle', formVehicle);
      formData.append('expense_category', formCategory);
      formData.append('requested_amount', formAmount);
      formData.append('vendor_name', formVendor);
      formData.append('expense_description', formDescription);
      formData.append('transport_manager_remarks', formRemarks);
      if (formOdoReading !== '' && formOdoReading !== null) {
        formData.append('odo_reading', formOdoReading);
      }

      if (formQuotation) formData.append('quotation_document', formQuotation);
      if (formInitialBill) formData.append('supporting_bill_document', formInitialBill);

      if (formSubmitDirectly) {
        formData.append('submit_now', 'true');
      }

      let res;
      if (editingExpense) {
        res = await vehicleExpenseAPI.updateExpense(editingExpense.id, formData);
        setSuccessMsg(`Expense request ${res.request_id || res.request_number || 'updated'} successfully.`);
      } else {
        res = await vehicleExpenseAPI.createExpense(formData);
        const reqCode = res.request_id || res.request_number || 'created';
        if (formSubmitDirectly) {
          setSuccessMsg(`Expense request ${reqCode} created and submitted to Finance.`);
        } else {
          setSuccessMsg(`Expense request ${reqCode} created as Draft.`);
        }
      }

      setShowRequestModal(false);
      fetchExpenses();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      console.error('Failed to save expense request:', err);
      const msg = err.response?.data?.detail || err.response?.data?.error || 'Failed to save expense request.';
      notify.success(msg);
    } finally {
      setFormSaving(false);
    }
  };

  const handleSubmitToFinance = async (id, reqNum) => {
    if (!await confirmDialog(`Are you sure you want to submit request ${reqNum} to Finance for approval?`)) return;
    setSubmittingId(id);
    try {
      await vehicleExpenseAPI.submitExpense(id);
      setSuccessMsg(`Request ${reqNum} submitted to Finance successfully.`);
      fetchExpenses();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      console.error('Submit error:', err);
      notify.error(err.response?.data?.detail || err.response?.data?.error || 'Failed to submit request.');
    } finally {
      setSubmittingId(null);
    }
  };

  const handleDeleteRequest = async (id, reqNum) => {
    if (!await confirmDialog(`Are you sure you want to delete draft request ${reqNum}?`)) return;
    try {
      await vehicleExpenseAPI.deleteExpense(id);
      setSuccessMsg(`Request ${reqNum} deleted.`);
      fetchExpenses();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      console.error('Delete error:', err);
      notify.error(err.response?.data?.detail || 'Failed to delete request.');
    }
  };

  const handleOpenCompleteModal = (exp) => {
    setCompletingExpense(exp);
    setFinalInvoice(null);
    setReceiptCopy(null);
    setRenewedDoc(null);
    setNewExpiryDate('');
    setCompleteNotes('');
    setShowCompleteModal(true);
  };

  const handleCompleteExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!finalInvoice) {
      notify.info('Please upload the Final Paid Invoice / Bill to complete settlement.');
      return;
    }

    const categoryKey = completingExpense?.expense_category || completingExpense?.category;
    const isStatutory = STATUTORY_CATEGORIES.includes(categoryKey);
    if (isStatutory && (!renewedDoc || !newExpiryDate)) {
      notify.success('This expense category is a statutory renewal (Insurance/PUC/Permit/Registration). Please upload the renewed document and enter the new expiry date.');
      return;
    }

    setCompleting(true);
    try {
      const formData = new FormData();
      formData.append('final_paid_invoice', finalInvoice);
      if (receiptCopy) formData.append('final_receipt', receiptCopy);
      if (renewedDoc) formData.append('renewed_vehicle_document', renewedDoc);
      if (newExpiryDate) formData.append('new_expiry_date', newExpiryDate);
      if (completeNotes) formData.append('transport_manager_remarks', completeNotes);

      const res = await vehicleExpenseAPI.completeExpense(completingExpense.id, formData);
      const reqCode = res.request_id || res.request_number || '';
      setSuccessMsg(`Expense ${reqCode} completed successfully! Vehicle record updated.`);
      setShowCompleteModal(false);
      fetchExpenses();
      setTimeout(() => setSuccessMsg(''), 5000);
    } catch (err) {
      console.error('Complete expense error:', err);
      notify.error(err.response?.data?.detail || err.response?.data?.error || 'Failed to complete expense settlement.');
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div style={{ padding: '4px 0' }}>
      {/* Header Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#0f172a' }}>
            🔧 Vehicle Maintenance & Expense Requests
          </h2>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            Submit vehicle maintenance bills, renewals & repairs to Finance for verification and fund release.
          </div>
        </div>
        <button
          onClick={handleOpenCreateModal}
          className="tm-btn-primary"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', fontSize: 14 }}
        >
          <span>➕</span> New Expense Request
        </button>
      </div>

      {successMsg && (
        <div style={{ marginBottom: 16, borderRadius: 10, padding: '12px 16px', background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>✓</span> <span>{successMsg}</span>
        </div>
      )}



      {/* Filters Bar */}
      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 20, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ flex: '1 1 200px' }}>
          <input
            type="text"
            placeholder="🔍 Search request no, vehicle reg, vendor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
          />
        </div>

        <select
          value={vehicleFilter}
          onChange={(e) => setVehicleFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#fff' }}
        >
          <option value="">All Vehicles</option>
          {vehicles.map((v) => (
            <option key={v.id} value={v.id}>{v.registration_number} ({v.model || v.make})</option>
          ))}
        </select>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#fff' }}
        >
          <option value="">All Categories</option>
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#fff' }}
        >
          <option value="">All Statuses</option>
          {Object.entries(STATUS_BADGES).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>📅 Date:</span>
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, background: '#fff' }}
            title="Filter by Date"
          />
        </div>

        {(statusFilter || categoryFilter || vehicleFilter || search || dateFilter) && (
          <button
            onClick={() => {
              setStatusFilter('');
              setCategoryFilter('');
              setVehicleFilter('');
              setSearch('');
              setDateFilter('');
            }}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#64748b', fontSize: 13, cursor: 'pointer' }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Requests Table */}
      <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b', fontSize: 14 }}>
            ⏳ Loading expense requests...
          </div>
        ) : error ? (
          <div style={{ padding: 30, textAlign: 'center', color: '#ef4444', fontSize: 14 }}>
            ⚠️ {error}
          </div>
        ) : expenses.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📝</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>No Vehicle Expense Requests Found</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Create a new request to submit maintenance or renewal costs to Finance.</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>
                  <th style={{ padding: '12px 16px' }}>Request ID / Date</th>
                  <th style={{ padding: '12px 16px' }}>Vehicle</th>
                  <th style={{ padding: '12px 16px' }}>Category</th>
                  <th style={{ padding: '12px 16px' }}>Requested (₹)</th>
                  <th style={{ padding: '12px 16px' }}>Approved (₹)</th>
                  <th style={{ padding: '12px 16px' }}>Status</th>
                  <th style={{ padding: '12px 16px' }}>Documents</th>
                  <th style={{ padding: '12px 16px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((exp) => {
                  const badge = STATUS_BADGES[exp.status] || { label: exp.status, bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' };
                  const createdDate = new Date(exp.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
                  const reqCode = exp.request_id || exp.request_number || `REQ-${exp.id}`;

                  // Extract URLs from serializer response
                  const quoteUrl = exp.quotation_document_url || exp.quotation_document;
                  const billUrl = exp.supporting_bill_document_url || exp.supporting_bill_document;
                  const finalInvoiceUrl = exp.final_paid_invoice_url || exp.final_paid_invoice;
                  const renewedDocUrl = exp.renewed_vehicle_document_url || exp.renewed_vehicle_document;

                  return (
                    <tr key={exp.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 700, color: '#0f172a' }}>{reqCode}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>{createdDate}</div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 700, color: '#1e293b' }}>🚘 {exp.vehicle_registration_number}</div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>
                          {exp.vehicle_details?.model || exp.vehicle_details?.make || exp.vehicle_model}
                          {(exp.odo_reading || exp.current_fuel_odo || exp.odometer_reading) ? ` • 📈 ODO: ${(exp.odo_reading || exp.current_fuel_odo || exp.odometer_reading).toLocaleString()} KM` : ''}
                        </div>
                        {exp.expense_category === 'FUEL' && exp.fuel_km_difference !== null && exp.fuel_km_difference !== undefined && (
                          <div style={{ marginTop: 2 }}>
                            <span style={{ fontSize: 10, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '1px 6px', borderRadius: 4, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <span>⛽</span> +{exp.fuel_km_difference.toLocaleString()} KM since refill
                            </span>
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ fontWeight: 600, color: '#334155' }}>
                          {exp.expense_category_display || exp.category_display || exp.expense_category}
                        </span>
                        {exp.vendor_name && (
                          <div style={{ fontSize: 11, color: '#64748b' }}>Vendor: {exp.vendor_name}</div>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: '#0f172a' }}>
                        ₹{parseFloat(exp.requested_amount).toLocaleString('en-IN')}
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: exp.approved_amount ? '#15803d' : '#94a3b8' }}>
                        {exp.approved_amount ? `₹${parseFloat(exp.approved_amount).toLocaleString('en-IN')}` : '—'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ padding: '4px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 700, background: badge.bg, color: badge.color, border: `1px solid ${badge.border}` }}>
                          {badge.label}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11 }}>
                          {quoteUrl && (
                            <a href={quoteUrl} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'underline' }}>
                              📄 Quotation
                            </a>
                          )}
                          {billUrl && (
                            <a href={billUrl} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'underline' }}>
                              🧾 Initial Bill
                            </a>
                          )}
                          {finalInvoiceUrl && (
                            <a href={finalInvoiceUrl} target="_blank" rel="noreferrer" style={{ color: '#16a34a', fontWeight: 700, textDecoration: 'underline' }}>
                              ✅ Paid Invoice
                            </a>
                          )}
                          {renewedDocUrl && (
                            <a href={renewedDocUrl} target="_blank" rel="noreferrer" style={{ color: '#0d9488', fontWeight: 700, textDecoration: 'underline' }}>
                              🛡️ Renewed Doc
                            </a>
                          )}
                          {!quoteUrl && !billUrl && !finalInvoiceUrl && !renewedDocUrl && (
                            <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No files</span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => setDetailExpense(exp)}
                            style={{ padding: '4px 8px', fontSize: 12, borderRadius: 6, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#334155', cursor: 'pointer' }}
                            title="View full audit log and details"
                          >
                            👁️ View Log
                          </button>

                          {(exp.status === 'DRAFT' || exp.status === 'CORRECTION_REQUESTED') && (
                            <>
                              <button
                                onClick={() => handleOpenEditModal(exp)}
                                style={{ padding: '4px 8px', fontSize: 12, borderRadius: 6, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer' }}
                              >
                                ✏️ Edit
                              </button>
                              <button
                                onClick={() => handleSubmitToFinance(exp.id, reqCode)}
                                disabled={submittingId === exp.id}
                                style={{ padding: '4px 8px', fontSize: 12, borderRadius: 6, border: '1px solid #fde68a', background: '#fef3c7', color: '#b45309', cursor: 'pointer', fontWeight: 600 }}
                              >
                                {submittingId === exp.id ? 'Submitting...' : '📤 Submit'}
                              </button>
                              {exp.status === 'DRAFT' && (
                                <button
                                  onClick={() => handleDeleteRequest(exp.id, reqCode)}
                                  style={{ padding: '4px 8px', fontSize: 12, borderRadius: 6, border: '1px solid #fca5a5', background: '#fee2e2', color: '#b91c1c', cursor: 'pointer' }}
                                >
                                  🗑️
                                </button>
                              )}
                            </>
                          )}

                          {exp.status === 'APPROVED' && (
                            <button
                              onClick={() => handleOpenCompleteModal(exp)}
                              style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, border: '1px solid #86efac', background: '#dcfce7', color: '#15803d', cursor: 'pointer', fontWeight: 700 }}
                            >
                              🏁 Settle & Complete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CREATE / EDIT REQUEST MODAL */}
      {showRequestModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#ffffff', borderRadius: 16, width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto', padding: 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: 12, marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                {editingExpense ? `✏️ Edit Request: ${editingExpense.request_id || editingExpense.request_number}` : '➕ New Vehicle Expense Request'}
              </h3>
              <button onClick={() => setShowRequestModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>

            <form onSubmit={handleSaveRequest}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                    Select Vehicle *
                  </label>
                  <select
                    value={formVehicle}
                    onChange={(e) => {
                      const vehId = e.target.value;
                      setFormVehicle(vehId);
                      const targetVeh = vehicles.find((v) => String(v.id) === String(vehId));
                      if (targetVeh) {
                        setFormOdoReading(targetVeh.odometer_reading || targetVeh.odo_meter_reading || '');
                      }
                      if (formCategory === 'FUEL' && vehId) {
                        fetchFuelInfo(vehId);
                      } else {
                        setFuelInfo(null);
                      }
                    }}
                    required
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                  >
                    <option value="">Select a Vehicle</option>
                    {vehicles.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.registration_number} ({v.model || v.make})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                    Expense Category *
                  </label>
                  <select
                    value={formCategory}
                    onChange={(e) => {
                      const newCat = e.target.value;
                      setFormCategory(newCat);
                      if (newCat === 'FUEL' && formVehicle) {
                        fetchFuelInfo(formVehicle);
                      } else {
                        setFuelInfo(null);
                      }
                    }}
                    required
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                  >
                    {CATEGORY_OPTIONS.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* FUEL REFILL VERIFICATION & MILEAGE STRIP */}
              {formCategory === 'FUEL' && (
                <div style={{
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
                  borderRadius: 12,
                  padding: '14px 16px',
                  color: '#ffffff',
                  marginBottom: 14,
                  boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, borderBottom: '1px solid rgba(255,255,255,0.2)', paddingBottom: 6 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#bfdbfe', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>⛽</span> Fuel Refill ODO Tracking & Verification
                    </div>
                    {loadingFuelInfo && (
                      <span style={{ fontSize: 10, background: 'rgba(255,255,255,0.2)', padding: '2px 6px', borderRadius: 4 }}>
                        ⏳ Checking prior refills...
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
                    <div style={{ background: 'rgba(255, 255, 255, 0.12)', borderRadius: 8, padding: '8px 10px', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                      <div style={{ fontSize: 10, color: '#bfdbfe' }}>Vehicle Current ODO</div>
                      <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>
                        {(vehicles.find(v => String(v.id) === String(formVehicle))?.odometer_reading || 0).toLocaleString()} KM
                      </div>
                    </div>

                    <div style={{ background: 'rgba(255, 255, 255, 0.12)', borderRadius: 8, padding: '8px 10px', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                      <div style={{ fontSize: 10, color: '#bfdbfe' }}>Last Recorded Refill ODO</div>
                      <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}>
                        {fuelInfo?.last_refill_odo !== null && fuelInfo?.last_refill_odo !== undefined ? (
                          `${fuelInfo.last_refill_odo.toLocaleString()} KM`
                        ) : (
                          <span style={{ fontSize: 12, color: '#e0e7ff' }}>Initial Baseline</span>
                        )}
                      </div>
                      {fuelInfo?.last_refill_request_id && (
                        <div style={{ fontSize: 10, color: '#bfdbfe', marginTop: 2 }}>
                          {fuelInfo.last_refill_request_id} ({fuelInfo.last_refill_date || ''})
                        </div>
                      )}
                    </div>

                    <div style={{ background: 'rgba(255, 255, 255, 0.22)', borderRadius: 8, padding: '8px 10px', border: '1px solid rgba(255, 255, 255, 0.3)' }}>
                      <div style={{ fontSize: 10, color: '#fef08a', fontWeight: 700 }}>KM Since Last Refill</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#fef08a', marginTop: 2 }}>
                        {(() => {
                          const curOdo = parseInt(formOdoReading, 10);
                          const lastOdo = fuelInfo?.last_refill_odo;
                          if (!isNaN(curOdo) && lastOdo !== null && lastOdo !== undefined) {
                            const diff = curOdo - lastOdo;
                            return diff >= 0 ? `+${diff.toLocaleString()} KM` : `⚠️ ${diff.toLocaleString()} KM`;
                          }
                          return 'First Refill';
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: formCategory === 'FUEL' ? 700 : 600, color: formCategory === 'FUEL' ? '#1d4ed8' : '#334155', marginBottom: 4 }}>
                    {formCategory === 'FUEL' ? '⛽ Current ODO Reading at Refill (KM) *' : 'Current ODO Meter Reading (KM)'}
                  </label>
                  <input
                    type="number"
                    min="0"
                    placeholder="e.g. 45200"
                    value={formOdoReading}
                    onChange={(e) => setFormOdoReading(e.target.value)}
                    required={formCategory === 'FUEL'}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: formCategory === 'FUEL' ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                      background: formCategory === 'FUEL' ? '#eff6ff' : '#ffffff',
                      fontWeight: formCategory === 'FUEL' ? 700 : 400,
                      fontSize: 13
                    }}
                  />
                  <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                    {formCategory === 'FUEL' ? 'Vehicle odometer reading when fuel was refilled.' : 'Vehicle ODO at time of expense/service.'}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                    Requested Amount (₹) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    placeholder="e.g. 25000"
                    value={formAmount}
                    onChange={(e) => setFormAmount(e.target.value)}
                    required
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                    Vendor / Workshop Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Tata Authorized Service Center"
                    value={formVendor}
                    onChange={(e) => setFormVendor(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                  Description / Details of Work
                </label>
                <textarea
                  rows={3}
                  placeholder="Describe the maintenance, repair scope, or renewal details..."
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
              </div>

              {/* Document Uploads */}
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 10 }}>
                  📄 Initial Supporting Documents / Bills / Quotations
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 4 }}>
                      Quotation / Estimate Document
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => setFormQuotation(e.target.files[0])}
                      style={{ fontSize: 12 }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: '#64748b', marginBottom: 4 }}>
                      Initial Bill / Invoice Copy
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(e) => setFormInitialBill(e.target.files[0])}
                      style={{ fontSize: 12 }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                  Additional Remarks / Justification
                </label>
                <input
                  type="text"
                  placeholder="Any remarks for Finance Department..."
                  value={formRemarks}
                  onChange={(e) => setFormRemarks(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
              </div>

              {!editingExpense && (
                <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    id="submitDirectly"
                    checked={formSubmitDirectly}
                    onChange={(e) => setFormSubmitDirectly(e.target.checked)}
                  />
                  <label htmlFor="submitDirectly" style={{ fontSize: 13, color: '#334155', fontWeight: 600 }}>
                    Submit directly to Finance for approval upon saving
                  </label>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setShowRequestModal(false)}
                  style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#475569', fontSize: 13, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formSaving}
                  className="tm-btn-primary"
                  style={{ padding: '8px 20px', fontSize: 13 }}
                >
                  {formSaving ? 'Saving...' : editingExpense ? 'Update Request' : formSubmitDirectly ? 'Submit to Finance' : 'Save as Draft'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* FINALIZE & COMPLETE MODAL */}
      {showCompleteModal && completingExpense && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#ffffff', borderRadius: 16, width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', padding: 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: 12, marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                  🏁 Finalize Settlement: {completingExpense.request_id || completingExpense.request_number}
                </h3>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                  Vehicle: <strong>{completingExpense.vehicle_registration_number}</strong> &bull; Approved Amount: <strong>₹{parseFloat(completingExpense.approved_amount).toLocaleString('en-IN')}</strong>
                </div>
              </div>
              <button onClick={() => setShowCompleteModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>

            <form onSubmit={handleCompleteExpenseSubmit}>
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 13, color: '#1e40af' }}>
                Please upload the final settlement paid invoice and receipt. If this was a statutory document renewal (Insurance, PUC, Permit, Registration), upload the renewed document and enter the new expiry date to automatically update the vehicle record.
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                  Final Paid Invoice / Bill *
                </label>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  required
                  onChange={(e) => setFinalInvoice(e.target.files[0])}
                  style={{ fontSize: 12, width: '100%' }}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                  Payment Receipt / Bank Transaction Copy
                </label>
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => setReceiptCopy(e.target.files[0])}
                  style={{ fontSize: 12, width: '100%' }}
                />
              </div>

              {STATUTORY_CATEGORIES.includes(completingExpense.expense_category || completingExpense.category) && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#166534', marginBottom: 10 }}>
                    🛡️ Update Statutory Vehicle Expiry & Document
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#166534', marginBottom: 4 }}>
                        Renewed Document File *
                      </label>
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        required
                        onChange={(e) => setRenewedDoc(e.target.files[0])}
                        style={{ fontSize: 12, width: '100%' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#166534', marginBottom: 4 }}>
                        New Expiry Date *
                      </label>
                      <input
                        type="date"
                        required
                        value={newExpiryDate}
                        onChange={(e) => setNewExpiryDate(e.target.value)}
                        style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #86efac', fontSize: 12 }}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                  Completion Notes / Remarks
                </label>
                <textarea
                  rows={2}
                  placeholder="Notes on expense settlement or maintenance completion..."
                  value={completeNotes}
                  onChange={(e) => setCompleteNotes(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setShowCompleteModal(false)}
                  style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#475569', fontSize: 13, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={completing}
                  style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                >
                  {completing ? 'Completing...' : '✅ Complete & Update Vehicle Record'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DETAIL & AUDIT LOG MODAL */}
      {detailExpense && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#ffffff', borderRadius: 16, width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto', padding: 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: 12, marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                  📜 Request Audit History: {detailExpense.request_id || detailExpense.request_number}
                </h3>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                  Vehicle: <strong>{detailExpense.vehicle_registration_number}</strong> &bull; Category: <strong>{detailExpense.expense_category_display || detailExpense.category_display}</strong>
                </div>
              </div>
              <button onClick={() => setDetailExpense(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>

            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 20, fontSize: 13 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>Requested Amount: <strong>₹{parseFloat(detailExpense.requested_amount).toLocaleString('en-IN')}</strong></div>
                <div>Approved Amount: <strong>{detailExpense.approved_amount ? `₹${parseFloat(detailExpense.approved_amount).toLocaleString('en-IN')}` : 'Not Approved'}</strong></div>
                <div>Created By: <strong>{detailExpense.created_by_name || 'Transport Manager'}</strong></div>
                <div>Approved By: <strong>{detailExpense.approved_by_name || 'Finance Dept'}</strong></div>
              </div>
              {detailExpense.finance_remarks && (
                <div style={{ marginTop: 10, borderTop: '1px solid #e2e8f0', paddingTop: 8, color: '#1e40af' }}>
                  <strong>Finance Remarks:</strong> {detailExpense.finance_remarks}
                </div>
              )}
            </div>

            <h4 style={{ fontSize: 14, fontWeight: 700, color: '#334155', margin: '0 0 12px' }}>
              ⏱️ Action Timeline & Audit Logs
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {detailExpense.audit_logs && detailExpense.audit_logs.length > 0 ? (
                detailExpense.audit_logs.map((log) => (
                  <div key={log.id} style={{ borderLeft: '3px solid #3b82f6', background: '#f8fafc', padding: '10px 14px', borderRadius: '0 8px 8px 0', fontSize: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: '#1e293b' }}>
                      <span>{log.action}</span>
                      <span style={{ color: '#64748b', fontWeight: 400 }}>{new Date(log.created_at).toLocaleString('en-IN')}</span>
                    </div>
                    <div style={{ color: '#475569', marginTop: 3 }}>
                      By: <strong>{log.action_by_name || 'System'}</strong>
                    </div>
                    {log.comments && (
                      <div style={{ color: '#334155', fontStyle: 'italic', marginTop: 4 }}>
                        &quot;{log.comments}&quot;
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>No audit history available.</div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
              <button
                onClick={() => setDetailExpense(null)}
                style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#f8fafc', color: '#334155', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportVehicleExpensesTab;
